import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MapPin, Users, Car, Crown, Check, ChevronsUpDown, Search, ArrowRight, ArrowLeftRight, Plane, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTransfers, TransferRoute, TransferLocation } from '@/hooks/useTransfers';
import { useVehicleImages } from '@/hooks/useVehicleImages';
import { useTranslatedText } from '@/hooks/useTranslatedText';
import sedanImage from '@/assets/sedan-transfer.jpg';
import suvImage from '@/assets/suv-transfer.jpg';
interface TransferBookingFormProps {
  preselectedDestination?: string;
}

export const TransferBookingForm = ({ preselectedDestination }: TransferBookingFormProps = {}) => {
  const navigate = useNavigate();
  const {
    allLocations,
    routes,
    loading,
    configLoading,
    availableDestinations,
    searchTransferPricing,
    calculatePrice,
    getAvailableDestinations,
    getAllFromLocations
  } = useTransfers();
  const {
    getVehicleByPassengers
  } = useVehicleImages();
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [tripType, setTripType] = useState<'oneway' | 'roundtrip'>('oneway');
  const [selectedRoute, setSelectedRoute] = useState<TransferRoute | null>(null);
  const [showPricing, setShowPricing] = useState(false);
  const [openFromCombo, setOpenFromCombo] = useState(false);
  const [openToCombo, setOpenToCombo] = useState(false);
  const [openPassengerCombo, setOpenPassengerCombo] = useState(false);
  const [fromLocations, setFromLocations] = useState<TransferLocation[]>([]);
  const {
    translatedText: bookYourTransferText
  } = useTranslatedText("Book Your Transfer");

  // Load from locations on component mount, when config loads, and when window gets focus
  useEffect(() => {
    // Don't load until config is ready
    if (configLoading) return;
    
    const loadFromLocations = async () => {
      const locations = await getAllFromLocations();
      setFromLocations(locations);
      
      // If there's a preselected destination, try to set it
      if (preselectedDestination && locations.length > 0) {
        const matchingLocation = locations.find(
          loc => loc.name.toLowerCase() === preselectedDestination.toLowerCase()
        );
        if (matchingLocation) {
          setToLocation(matchingLocation.id);
        }
      }
    };
    loadFromLocations();
    const handleFocus = () => {
      loadFromLocations();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [preselectedDestination, configLoading]);

  // Reset pricing display when passengers change
  useEffect(() => {
    if (passengers > 5 && showPricing) {
      if (selectedRoute) {
        setShowPricing(true);
      }
    }
  }, [passengers, showPricing, selectedRoute]);
  const handleSearch = async () => {
    if (!fromLocation || !toLocation) return;
    const results = await searchTransferPricing(fromLocation, toLocation);
    if (results.length > 0) {
      setSelectedRoute(results[0]);
      setShowPricing(true);
    }
  };
  useEffect(() => {
    if (fromLocation) {
      handleFromLocationChange(fromLocation);
    }
  }, [fromLocation]);
  const handleFromLocationChange = async (locationId: string) => {
    const fromLocationObj = [...fromLocations, ...allLocations].find(l => l.id === locationId);
    if (!fromLocationObj) return;
    if (fromLocationObj.type === 'hotel' && fromLocationObj.airportZone) {
      const correspondingAirport = [...fromLocations, ...allLocations].find(l => l.type === 'airport' && l.tableName === fromLocationObj.airportZone);
      if (correspondingAirport) {
        setToLocation(correspondingAirport.id);
      }
    } else {
      setToLocation('');
    }
    await getAvailableDestinations(locationId);
    setShowPricing(false);
    setSelectedRoute(null);
  };
  const handleServiceSelect = (serviceType: 'standard' | 'vip') => {
    if (!selectedRoute) return;
    const price = calculatePrice(selectedRoute, passengers, serviceType, tripType);
    navigate('/booking', {
      state: {
        from: [...fromLocations, ...allLocations].find(l => l.id === fromLocation)?.name,
        to: [...fromLocations, ...allLocations, ...availableDestinations].find(l => l.id === toLocation)?.name,
        passengers,
        tripType,
        serviceType,
        price,
        route: selectedRoute
      }
    });
  };
  return <section className="w-full py-4">
      <div className="relative w-full max-w-5xl mx-auto px-4">
        {/* Decorative Background */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 rounded-3xl blur-3xl"></div>
        
        {/* Hero Header */}
        

        {/* Main Form Container */}
        <div className="bg-background/80 backdrop-blur-sm rounded-3xl border-2 border-accent/20 shadow-2xl p-6 md:p-8">
          {/* Trip Type Selection */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <button onClick={() => setTripType('oneway')} className={cn("relative p-6 rounded-xl border-2 transition-all duration-300", tripType === 'oneway' ? "border-primary bg-gradient-to-br from-primary/10 to-accent/10 shadow-lg shadow-primary/20" : "border-border hover:border-primary/50 bg-background/50")}>
              <div className="flex items-center justify-center gap-3 mb-2">
                <ArrowRight className={cn("h-6 w-6", tripType === 'oneway' ? "text-primary" : "text-muted-foreground")} />
                <span className={cn("text-lg font-semibold", tripType === 'oneway' ? "text-primary" : "text-foreground")}>
                  One Way
                </span>
              </div>
              {tripType === 'oneway' && <div className="absolute top-3 right-3">
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                </div>}
            </button>

            <button onClick={() => setTripType('roundtrip')} className={cn("relative p-6 rounded-xl border-2 transition-all duration-300", tripType === 'roundtrip' ? "border-primary bg-gradient-to-br from-primary/10 to-accent/10 shadow-lg shadow-primary/20" : "border-border hover:border-primary/50 bg-background/50")}>
              <div className="flex items-center justify-center gap-3 mb-2">
                <ArrowLeftRight className={cn("h-6 w-6", tripType === 'roundtrip' ? "text-primary" : "text-muted-foreground")} />
                <span className={cn("text-lg font-semibold", tripType === 'roundtrip' ? "text-primary" : "text-foreground")}>
                  Round Trip
                </span>
              </div>
              {tripType === 'roundtrip' && <div className="absolute top-3 right-3">
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                </div>}
            </button>
          </div>

          {/* Selection Fields Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* From Location */}
            <div className="space-y-3">
              <label className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                From Location
              </label>
              <Popover open={openFromCombo} onOpenChange={setOpenFromCombo}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between h-14 text-base border-2 hover:border-primary/50 transition-all bg-background/50">
                    <div className="flex items-center gap-3">
                      <Plane className="h-5 w-5 text-primary" />
                      <span className="font-medium truncate">
                        {fromLocation ? allLocations.find(l => l.id === fromLocation)?.name : "Select pickup..."}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0 backdrop-blur-xl bg-background/95 border-2 border-accent/30 shadow-2xl z-50">
                  <Command>
                    <CommandInput placeholder="Search locations..." />
                    <CommandList>
                      <CommandEmpty>No location found.</CommandEmpty>
                      <CommandGroup heading="Airports">
                        {fromLocations.filter(location => location.type === 'airport').map(location => <CommandItem key={location.id} value={location.name} onSelect={() => {
                        setFromLocation(location.id);
                        setOpenFromCombo(false);
                      }} className="cursor-pointer hover:bg-primary/10">
                            <Check className={cn("mr-2 h-4 w-4", fromLocation === location.id ? "opacity-100 text-primary" : "opacity-0")} />
                            <MapPin className="mr-2 h-4 w-4 text-primary" />
                            {location.name}
                            <Badge variant="default" className="ml-auto">Airport</Badge>
                          </CommandItem>)}
                      </CommandGroup>
                      <CommandGroup heading="Hotels">
                        {fromLocations.filter(location => location.type === 'hotel').map(location => <CommandItem key={location.id} value={location.name} onSelect={() => {
                        setFromLocation(location.id);
                        setOpenFromCombo(false);
                      }} className="cursor-pointer hover:bg-primary/10">
                            <Check className={cn("mr-2 h-4 w-4", fromLocation === location.id ? "opacity-100 text-primary" : "opacity-0")} />
                            <MapPin className="mr-2 h-4 w-4 text-accent" />
                            {location.name}
                            <Badge variant="secondary" className="ml-auto">Hotel</Badge>
                          </CommandItem>)}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* To Location */}
            <div className="space-y-3">
              <label className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                To Location
              </label>
              <Popover open={openToCombo} onOpenChange={setOpenToCombo}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" disabled={!fromLocation || availableDestinations.length === 0} className="w-full justify-between h-14 text-base border-2 hover:border-primary/50 transition-all bg-background/50 disabled:opacity-50">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-accent" />
                      <span className="font-medium truncate">
                        {toLocation ? [...fromLocations, ...allLocations, ...availableDestinations].find(l => l.id === toLocation)?.name : availableDestinations.length === 0 ? "Select from first..." : "Select destination..."}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0 backdrop-blur-xl bg-background/95 border-2 border-accent/30 shadow-2xl z-50">
                  <Command>
                    <CommandInput placeholder="Search destinations..." />
                    <CommandList>
                      <CommandEmpty>No destination found.</CommandEmpty>
                      <CommandGroup>
                        {availableDestinations.map(location => <CommandItem key={location.id} value={location.name} onSelect={() => {
                        setToLocation(location.id);
                        setOpenToCombo(false);
                      }} className="cursor-pointer hover:bg-primary/10">
                            <Check className={cn("mr-2 h-4 w-4", toLocation === location.id ? "opacity-100 text-primary" : "opacity-0")} />
                            <MapPin className="mr-2 h-4 w-4" />
                            {location.name}
                            <Badge variant={location.type === 'airport' ? 'default' : 'secondary'} className="ml-auto">
                              {location.type}
                            </Badge>
                          </CommandItem>)}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Passengers */}
            <div className="space-y-3">
              <label className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Passengers
              </label>
              <Popover open={openPassengerCombo} onOpenChange={setOpenPassengerCombo}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between h-14 text-base border-2 hover:border-primary/50 transition-all bg-background/50">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-secondary" />
                      <span className="font-medium">
                        {passengers} {passengers === 1 ? 'Passenger' : 'Passengers'}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0 backdrop-blur-xl bg-background/95 border-2 border-accent/30 shadow-2xl z-50">
                  <Command>
                    <CommandList>
                      <CommandGroup>
                        {[...Array(12)].map((_, i) => <CommandItem key={i + 1} value={`${i + 1} passengers`} onSelect={() => {
                        setPassengers(i + 1);
                        setOpenPassengerCombo(false);
                      }} className="cursor-pointer hover:bg-primary/10">
                            <Check className={cn("mr-2 h-4 w-4", passengers === i + 1 ? "opacity-100 text-primary" : "opacity-0")} />
                            <Users className="mr-2 h-4 w-4" />
                            {i + 1} {i === 0 ? 'Passenger' : 'Passengers'}
                          </CommandItem>)}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Search Button */}
          <Button onClick={handleSearch} disabled={!fromLocation || !toLocation || loading} className="w-full h-16 text-lg font-semibold bg-gradient-to-r from-primary via-accent to-secondary hover:shadow-2xl hover:shadow-primary/30 transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 group">
            <Search className="mr-3 h-6 w-6 group-hover:animate-pulse" />
            {loading ? 'Searching...' : 'Search Available Transfers'}
          </Button>
        </div>

        {/* Results Section */}
        {showPricing && selectedRoute && <div className="mt-12 animate-fade-in">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
                Available Services
              </h3>
              <p className="text-muted-foreground text-lg">
                Choose the perfect service for your journey
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Standard Service Card */}
              {(() => {
            const standardVehicle = getVehicleByPassengers('standard', passengers);
            return <div className="group bg-background rounded-2xl border-2 border-accent/30 overflow-hidden hover:border-primary transition-all duration-300 hover:shadow-2xl hover:shadow-primary/20 hover:-translate-y-2">
                    <div className="relative h-56 overflow-hidden bg-muted/20">
                      <img src={standardVehicle?.image_url || sedanImage} alt="Standard Transfer" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent"></div>
                      <div className="absolute top-4 left-4">
                        <Badge className="px-4 py-2 bg-primary/90 backdrop-blur-sm text-white text-sm font-semibold shadow-lg">
                          Standard
                        </Badge>
                      </div>
                      <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-background/90 backdrop-blur-sm px-3 py-1.5 rounded-full">
                        <Users className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">{passengers} pax</span>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h4 className="text-2xl font-bold text-foreground mb-3">Comfortable Ride</h4>
                      <p className="text-muted-foreground mb-4">
                        {standardVehicle?.description || "Comfortable sedan or similar"}
                      </p>
                      <ul className="space-y-2 mb-6">
                        {(standardVehicle?.features || ['Professional driver', 'Air conditioning', 'Luggage included']).map((feature, index) => <li key={index} className="flex items-center gap-2 text-muted-foreground">
                            <Check className="h-5 w-5 text-primary flex-shrink-0" />
                            <span>{feature}</span>
                          </li>)}
                      </ul>
                      
                      <div className="flex items-end justify-between mb-4">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">From</p>
                          <p className="text-4xl font-bold text-primary">
                            ${calculatePrice(selectedRoute, passengers, 'standard', tripType).toFixed(2)}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {tripType === 'roundtrip' ? 'Round trip' : 'One way'}
                          </p>
                        </div>
                      </div>

                      <Button onClick={() => handleServiceSelect('standard')} className="w-full h-12 bg-gradient-to-r from-primary to-accent hover:shadow-lg hover:shadow-primary/30 transition-all duration-300">
                        Select Service
                      </Button>
                    </div>
                  </div>;
          })()}

              {/* VIP Service Card */}
              {(() => {
            const vipVehicle = getVehicleByPassengers('vip', passengers);
            if (!vipVehicle) return null;
            return <div className="group bg-background rounded-2xl border-2 border-secondary/40 overflow-hidden hover:border-secondary transition-all duration-300 hover:shadow-2xl hover:shadow-secondary/20 hover:-translate-y-2">
                    <div className="relative h-56 overflow-hidden bg-muted/20">
                      <img src={vipVehicle.image_url || suvImage} alt="VIP Transfer" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent"></div>
                      <div className="absolute top-4 left-4">
                        <Badge className="px-4 py-2 bg-gradient-to-r from-secondary to-secondary/80 backdrop-blur-sm text-white text-sm font-semibold shadow-lg flex items-center gap-2">
                          <Crown className="h-4 w-4" />
                          VIP
                        </Badge>
                      </div>
                      <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-background/90 backdrop-blur-sm px-3 py-1.5 rounded-full">
                        <Users className="h-4 w-4 text-secondary" />
                        <span className="text-sm font-medium">{passengers} pax</span>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h4 className="text-2xl font-bold text-foreground mb-3">Luxury Experience</h4>
                      <p className="text-muted-foreground mb-4">
                        {vipVehicle.description || "Luxury SUV or similar"}
                      </p>
                      <ul className="space-y-2 mb-6">
                        {vipVehicle.features.map((feature, index) => <li key={index} className="flex items-center gap-2 text-muted-foreground">
                            <Check className="h-5 w-5 text-secondary flex-shrink-0" />
                            <span>{feature}</span>
                          </li>)}
                      </ul>
                      
                      <div className="flex items-end justify-between mb-4">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">From</p>
                          <p className="text-4xl font-bold text-secondary">
                            ${calculatePrice(selectedRoute, passengers, 'vip', tripType).toFixed(2)}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {tripType === 'roundtrip' ? 'Round trip' : 'One way'}
                          </p>
                        </div>
                      </div>

                      <Button onClick={() => handleServiceSelect('vip')} className="w-full h-12 bg-gradient-to-r from-secondary to-secondary/80 hover:shadow-lg hover:shadow-secondary/30 transition-all duration-300">
                        Select Service
                      </Button>
                    </div>
                  </div>;
          })()}
            </div>
          </div>}
      </div>
    </section>;
};